import {
  type ScriptableTooltipContext,
  type TooltipItem,
  type TooltipModel,
} from "chart.js";

import { KpiType } from "@features/mmm/components/explore-budgets/client-setting/types";
import { buildLegend } from "@features/sales-v3/utils/tooltip";
import { MetricFormatters } from "@north-beam/nb-client-side";

import {
  DATASET_ACTUAL_LABEL,
  DATASET_PREDICTION_LABEL,
  DATASET_PREDICTION_RANGE_LABEL,
} from "./utils/chart-dataset-helpers";

export function chartTooltip(
  context: ScriptableTooltipContext<"line">,
  kpiType: KpiType,
) {
  const chart = context.chart;
  const tooltip = context.tooltip;
  const options = context.tooltip.options as any;
  const isRevenue = kpiType === KpiType.REVENUE;
  let tooltipEl = document.getElementById("chartjs-tooltip");

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-tooltip";
    tooltipEl.innerHTML = "<div></div>";
    document.body.appendChild(tooltipEl);
  }

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    return;
  }

  if (tooltip.body && tooltip.dataPoints.length) {
    const content = buildTooltipContent(tooltip, isRevenue, options);

    const arrowPosition = {
      left: "left-0 translate-x-[-50%]",
      center: "right-0 translate-x-[50%]",
      right: "right-0 translate-x-[50%]",
    }[tooltip.xAlign];

    const contentPosition = {
      left: "translate-x-[-30px]",
      center: "translate-x-[50%]",
      right: "translate-x-[30px]",
    }[tooltip.xAlign];

    tooltipEl.innerHTML = `
      <div class="relative">
        <div
          data-tooltip-id="content"
          style="min-width: 330px;"
          class="
            bg-v2-primary border border-v2-secondary py-5 px-4 rounded-md space-y-2.5
            shadow whitespace-nowrap ${contentPosition}
          "
        >
          <div class="text-lg font-bold leading-0 py-2">${options.tooltipTitle}</div>
          <div class="text-xs text-gray+1">${tooltip.dataPoints[0].label}</div>
          ${content}
        </div>
        <div
          data-tooltip-id="arrow"
          class="
            h-3 w-3 bg-v2-primary absolute bottom-[-6px] border-t border-l border-v2-secondary
            ${arrowPosition} rotate-[225deg]
          "
        >
        </div>
      </div>
    `;
  }

  const position = chart.canvas.getBoundingClientRect();

  const translateX = { left: "0%", center: "-100%", right: "-100%" }[
    tooltip.xAlign
  ];

  Object.assign(tooltipEl.style, {
    position: "absolute",
    opacity: "1",
    zIndex: "3000",
    pointerEvents: "none",
    paddingBottom: "12px",
    transform: `translate(${translateX}, -100%)`,
    top: `${position.top + window.scrollY + tooltip.caretY}px`,
    left: `${position.left + window.scrollX + tooltip.caretX}px`,
  });

  const contentEl = tooltipEl.querySelector("[data-tooltip-id='content']");
  const arrowEl = tooltipEl.querySelector("[data-tooltip-id='arrow']");

  if (contentEl && arrowEl) {
    const contentRect = contentEl.getBoundingClientRect();

    if (contentRect.left < 0) {
      arrowEl.classList.remove(
        "left-0",
        "right-0",
        "translate-x-[50%]",
        "translate-x-[-50%]",
      );
      arrowEl.classList.add("left-0", "translate-x-[-50%]");

      contentEl.classList.remove(
        "translate-x-[30px]",
        "translate-x-[-30px]",
        "translate-x-[50%]",
      );
      contentEl.classList.add("translate-x-[-30px]");
      tooltipEl.style.transform = "translate(0%, -100%)";
    } else if (contentRect.right > window.innerWidth) {
      arrowEl.classList.remove(
        "left-0",
        "right-0",
        "translate-x-[50%]",
        "translate-x-[-50%]",
      );
      arrowEl.classList.add("right-0", "translate-x-[50%]");

      contentEl.classList.remove(
        "translate-x-[30px]",
        "translate-x-[-30px]",
        "translate-x-[50%]",
      );
      contentEl.classList.add("translate-x-[30px]");
    }
  }
}

const buildTooltipContent = (
  tooltip: TooltipModel<"line" | "bar">,
  isRevenue: boolean,
  options: any,
) => {
  const content = [];

  const actual = tooltip.dataPoints.find(
    ({ dataset }) => dataset.label === DATASET_ACTUAL_LABEL,
  );
  const predicted = tooltip.dataPoints.find(
    ({ dataset }) => dataset.label === DATASET_PREDICTION_LABEL,
  );
  const predictedRange = tooltip.dataPoints.filter(
    ({ dataset }) => dataset.label === DATASET_PREDICTION_RANGE_LABEL,
  );

  if (actual) {
    content.push(buildActualLegend(options, isRevenue, actual, predicted));
  }

  if (predicted) {
    content.push(
      buildPredictedLegend(options, isRevenue, predicted, predictedRange),
    );
  }

  const divider = `<div class="${"border-t border-v2-secondary h-1 my-2 w-full"}"></div>`;

  return `
    <div class="flex flex-col text-xs space-y-3">
      ${content.join(divider)}
    </div>
  `;
};

const buildActualLegend = (
  options: any,
  isRevenue: boolean,
  actual: TooltipItem<"line">,
  predicted?: TooltipItem<"line">,
) => {
  const legend = buildLegend(
    {
      borderColor: actual.dataset.borderColor,
      backgroundColor: actual.dataset.backgroundColor,
      borderDash: actual.dataset.borderDash ?? [],
      borderWidth: 4,
      pointRadius: 5,
    } as any,
    false,
  );

  const formattedValueTotal = isRevenue
    ? MetricFormatters.currency(
        actual.parsed.y,
        options.user.currencies.currentCurrency,
        {
          precision: "approximate",
        },
      )
    : MetricFormatters.decimal(
        actual.parsed.y,
        options.user.currencies.currentCurrency,
        {
          precision: "approximate",
        },
      );

  let content = `
    <div class="flex space-x-0.5 justify-between">
      <div class="flex space-x-0.5 items-center">
        <div style="width: 22px;">${legend}</div>
        <div>${actual.dataset.label}</div>
      </div>
      <div class="text-right font-mono">${formattedValueTotal}</div>
    </div>
  `;

  if (predicted) {
    const diff = (actual.raw as number) / (predicted.raw as number) - 1;
    const diffFormatted = MetricFormatters.percentage(
      Math.abs(diff),
      options.user.currencies.currentCurrency,
      {
        precision: "approximate",
      },
    );

    content += `
      <div class="text-gray+1 ml-6">
        <span class="font-bold">${diffFormatted}</span>
        <span>${diff > 0 ? "Above" : "Below"} predicted</span>
      </div>
    `;
  }

  return content;
};

const buildPredictedLegend = (
  options: any,
  isRevenue: boolean,
  { dataset, parsed }: TooltipItem<"line">,
  range: TooltipItem<"line">[],
) => {
  const legend = buildLegend(
    {
      borderColor: dataset.borderColor,
      backgroundColor: dataset.backgroundColor,
      borderDash: dataset.borderDash ?? [],
      borderWidth: 4,
      pointRadius: 5,
    } as any,
    false,
  );

  const predictedLegend = buildLegend(
    {
      borderColor: "#D5D9FE",
      backgroundColor: "#D5D9FE",
      borderDash: [],
    } as any,
    true,
  );

  const rangeMin = Math.min(...range.map((e) => e.parsed.y));
  const rangeMax = Math.max(...range.map((e) => e.parsed.y));

  const rangeMinFormatted = isRevenue
    ? MetricFormatters.currency(
        Math.abs(rangeMin),
        options.user.currencies.currentCurrency,
        {
          precision: "approximate",
        },
      )
    : MetricFormatters.decimal(
        Math.abs(rangeMax),
        options.user.currencies.currentCurrency,
        {
          precision: "approximate",
        },
      );
  const rangeMaxFormatted = isRevenue
    ? MetricFormatters.currency(
        Math.abs(rangeMax),
        options.user.currencies.currentCurrency,
        {
          precision: "approximate",
        },
      )
    : MetricFormatters.decimal(
        Math.abs(rangeMax),
        options.user.currencies.currentCurrency,
        {
          precision: "approximate",
        },
      );

  const formattedValueTotal = isRevenue
    ? MetricFormatters.currency(
        parsed.y,
        options.user.currencies.currentCurrency,
        {
          precision: "approximate",
        },
      )
    : MetricFormatters.decimal(
        parsed.y,
        options.user.currencies.currentCurrency,
        {
          precision: "approximate",
        },
      );
  return `
    <div class="flex space-x-0.5 justify-between mb-2">
      <div class="flex space-x-0.5 items-center">
        <div style="width: 22px;">${legend}</div>
        <div>${dataset.label}</div>
      </div>
      <div class="text-right font-mono">${formattedValueTotal}</div>
    </div>
    <div class="flex space-x-0.5 justify-between">
      <div class="flex space-x-0.5 items-center">
        <div style="width: 22px;">${predictedLegend}</div>
        <div>Predicted range</div>
      </div>
      <div class="text-right font-mono">${rangeMinFormatted} - ${rangeMaxFormatted}</div>
    </div>
  `;
};
